import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useClubLogo } from '../lib/useClubLogo';

const preloadImage = (src) => new Promise((resolve) => {
  const img = new Image();
  img.onload = resolve;
  img.onerror = resolve;
  img.src = src;
});

/* ── 3D Puck ── */
function HockeyPuck({ size, flipped, bling, logoLoaded, logoUrl }) {
  const LOGO_URL = logoUrl;
  const LAYERS = 8;
  const DEPTH = 14;
  const step = DEPTH / LAYERS;
  return (
    <div style={{ perspective: '600px', perspectiveOrigin: '50% 48%' }}>
      <motion.div
        style={{ width: size, height: size, transformStyle: 'preserve-3d', position: 'relative' }}
        animate={{ rotateY: flipped ? -180 : 0, rotateX: flipped ? -5 : 12 }}
        transition={{ rotateY: { duration: flipped ? 1.0 : 0, ease: [0.4, 0, 0.2, 1] }, rotateX: { duration: 0.5 } }}
      >
        <div className="absolute inset-0 rounded-full" style={{
          backfaceVisibility: 'hidden', transform: `translateZ(${DEPTH / 2}px)`,
          background: 'radial-gradient(ellipse at 38% 30%, #3a3a3a 0%, #1a1a1a 45%, #0a0a0a 100%)',
          boxShadow: 'inset 0 2px 6px rgba(255,255,255,0.06), inset 0 -3px 8px rgba(0,0,0,0.6)',
        }}>
          <div className="absolute inset-[10%] rounded-full border border-white/[0.04]" />
          <div className="absolute inset-[20%] rounded-full border border-white/[0.03]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[#151515] border border-white/[0.05]" />
        </div>
        <div className="absolute inset-0 rounded-full overflow-hidden" style={{
          backfaceVisibility: 'hidden', transform: `translateZ(${-DEPTH / 2 - 1}px) rotateY(-180deg)`,
          background: '#111', boxShadow: '0 0 25px rgba(204,0,0,0.35)', border: '2px solid rgba(204,0,0,0.35)', zIndex: 10,
        }}>
          {logoLoaded && <img src={LOGO_URL} alt="Devils" className="w-full h-full object-contain p-1.5 rounded-full" style={{ background: '#141414' }} />}
          {bling && (
            <motion.div className="absolute inset-0 rounded-full"
              style={{ background: 'linear-gradient(105deg, transparent 25%, rgba(255,255,255,0.7) 46%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.7) 54%, transparent 75%)' }}
              initial={{ x: '-200%' }} animate={{ x: '200%' }} transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }} />
          )}
        </div>
        {Array.from({ length: LAYERS }).map((_, i) => (
          <div key={i} className="absolute inset-0 rounded-full" style={{
            transform: `translateZ(${-DEPTH / 2 + i * step}px)`,
            background: 'radial-gradient(ellipse, rgba(28,28,28,0.95) 85%, rgba(18,18,18,1) 100%)', border: '1px solid rgba(38,38,38,0.7)',
          }} />
        ))}
      </motion.div>
    </div>
  );
}

/* ── Goal Net with impact deformation ── */
function GoalNet({ impacted }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <motion.div style={{ width: '480px', height: '320px' }} className="relative"
        animate={impacted ? { x: [0, -3, 4, -2, 1, 0], y: [0, 2, -2, 1, 0] } : {}}
        transition={{ duration: 0.3 }}>
        {/* Frame */}
        <div className="absolute top-[10%] left-[10%] right-[10%] h-[3px]" style={{ background: 'linear-gradient(90deg, rgba(180,0,0,0.15), rgba(220,0,0,0.45), rgba(180,0,0,0.15))' }} />
        <div className="absolute top-[10%] left-[10%] bottom-[14%] w-[3px]" style={{ background: 'linear-gradient(180deg, rgba(220,0,0,0.45), rgba(150,0,0,0.1))' }} />
        <div className="absolute top-[10%] right-[10%] bottom-[14%] w-[3px]" style={{ background: 'linear-gradient(180deg, rgba(220,0,0,0.45), rgba(150,0,0,0.1))' }} />

        {/* Net mesh - horizontal with impact bulge */}
        {Array.from({ length: 12 }).map((_, i) => {
          const yPos = 11 + i * 6;
          const distFromCenter = Math.abs(yPos - 45);
          const bulgeAmount = impacted ? Math.max(0, 20 - distFromCenter) * 0.4 : 0;
          return (
            <motion.div key={`h-${i}`} className="absolute left-[11%] right-[11%]"
              style={{ top: `${yPos}%`, height: '1px', background: `rgba(255,255,255,${0.02 + (i < 6 ? 0.008 : 0)})` }}
              animate={impacted ? {
                transform: [`scaleY(1) translateZ(0px)`, `scaleY(${1 + bulgeAmount * 0.05}) translateZ(${bulgeAmount}px)`, `scaleY(1) translateZ(0px)`],
              } : {}}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          );
        })}
        {Array.from({ length: 15 }).map((_, i) => {
          const xPos = 11 + i * 5.2;
          const distFromCenter = Math.abs(xPos - 50);
          const bulgeAmount = impacted ? Math.max(0, 18 - distFromCenter) * 0.3 : 0;
          return (
            <motion.div key={`v-${i}`} className="absolute top-[10%] bottom-[14%]"
              style={{ left: `${xPos}%`, width: '1px', background: `rgba(255,255,255,${0.02 + (i > 3 && i < 12 ? 0.008 : 0)})` }}
              animate={impacted ? {
                transform: [`translateZ(0px)`, `translateZ(${bulgeAmount}px)`, `translateZ(0px)`],
              } : {}}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          );
        })}

        {/* Big visible net bulge / stretch effect at impact center */}
        {impacted && (
          <motion.div className="absolute" style={{ top: '25%', left: '25%', width: '50%', height: '50%' }}>
            {/* Ripple rings outward from center */}
            {[0, 1, 2].map((ring) => (
              <motion.div key={`ripple-${ring}`}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border"
                style={{ borderColor: `rgba(255,255,255,${0.06 - ring * 0.015})` }}
                initial={{ width: 0, height: 0, opacity: 0.5 }}
                animate={{ width: 80 + ring * 60, height: 80 + ring * 60, opacity: 0 }}
                transition={{ duration: 0.6, delay: ring * 0.08, ease: 'easeOut' }}
              />
            ))}
            {/* Impact push-back glow */}
            <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{ width: '120px', height: '120px', background: 'radial-gradient(circle, rgba(255,255,255,0.06), transparent 60%)' }}
              initial={{ scale: 0 }} animate={{ scale: [0, 1.5, 1], opacity: [0, 0.6, 0.15] }}
              transition={{ duration: 0.5 }}
            />
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}

/* ── Broadcast Wipe Transition (diagonal bars at END - reveals website behind) ── */
function BroadcastWipe({ onDone }) {
  return (
    <div className="fixed inset-0 z-[100] pointer-events-none overflow-hidden">
      {/* Bar 1: Red diagonal - sweeps across and exits right */}
      <motion.div className="absolute inset-0"
        style={{ background: '#CC0000', clipPath: 'polygon(0 0, 0% 0, 0% 100%, 0 100%)' }}
        animate={{
          clipPath: [
            'polygon(0 0, 0% 0, 0% 100%, 0 100%)',
            'polygon(0 0, 55% 0, 40% 100%, 0 100%)',
            'polygon(50% 0, 115% 0, 100% 100%, 35% 100%)',
            'polygon(100% 0, 115% 0, 115% 100%, 100% 100%)',
          ]
        }}
        transition={{ duration: 1.0, times: [0, 0.35, 0.7, 1], ease: [0.4, 0, 0.2, 1] }}
      >
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 20px, rgba(255,255,255,0.1) 20px, rgba(255,255,255,0.1) 21px)' }} />
      </motion.div>

      {/* Bar 2: White thin accent - follows red */}
      <motion.div className="absolute inset-0"
        style={{ background: 'rgba(255,255,255,0.95)' }}
        initial={{ clipPath: 'polygon(0 0, 0% 0, 0% 100%, 0 100%)' }}
        animate={{
          clipPath: [
            'polygon(0 0, 0% 0, 0% 100%, 0 100%)',
            'polygon(42% 0, 58% 0, 43% 100%, 27% 100%)',
            'polygon(90% 0, 108% 0, 103% 100%, 85% 100%)',
            'polygon(108% 0, 112% 0, 112% 100%, 108% 100%)',
          ]
        }}
        transition={{ duration: 1.0, delay: 0.06, times: [0, 0.35, 0.7, 1], ease: [0.4, 0, 0.2, 1] }}
      />

      {/* Bar 3: Dark/black - sweeps from right and exits right */}
      <motion.div className="absolute inset-0"
        style={{ background: '#050506' }}
        initial={{ clipPath: 'polygon(100% 0, 100% 0, 100% 100%, 100% 100%)' }}
        animate={{
          clipPath: [
            'polygon(100% 0, 100% 0, 100% 100%, 100% 100%)',
            'polygon(45% 0, 100% 0, 100% 100%, 30% 100%)',
            'polygon(95% 0, 120% 0, 120% 100%, 80% 100%)',
            'polygon(110% 0, 120% 0, 120% 100%, 110% 100%)',
          ]
        }}
        transition={{ duration: 1.0, delay: 0.03, times: [0, 0.35, 0.7, 1], ease: [0.4, 0, 0.2, 1] }}
      />
    </div>
  );
}

/* ── Impact Sparks ── */
function ImpactSparks() {
  const sparks = useMemo(() =>
    Array.from({ length: 20 }, (_, i) => {
      const a = (i * 18) + Math.random() * 10;
      const r = (a * Math.PI) / 180;
      const d = 40 + Math.random() * 90;
      return { id: i, x: Math.cos(r) * d, y: Math.sin(r) * d, d: Math.random() * 0.1, c: Math.random() > 0.5 ? '#46C7FF' : '#FFD700' };
    }), []);
  return (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30">
      {sparks.map(s => (
        <motion.div key={s.id} className="absolute w-1 h-1 rounded-full"
          style={{ background: s.c, boxShadow: `0 0 4px ${s.c}` }}
          initial={{ x: 0, y: 0, opacity: 1 }}
          animate={{ x: s.x, y: s.y, opacity: 0, scale: 0 }}
          transition={{ duration: 0.5, delay: s.d, ease: 'easeOut' }} />
      ))}
      <motion.div className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{ width: 220, height: 220, background: 'radial-gradient(circle, rgba(255,255,255,0.5), transparent 50%)' }}
        initial={{ scale: 0 }} animate={{ scale: [0, 2.2], opacity: [1, 0] }} transition={{ duration: 0.4 }} />
    </div>
  );
}

/* ── Main ── */
export default function IntroAnimation({ onComplete }) {
  const LOGO_URL = useClubLogo();
  const [phase, setPhase] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const t = [
      setTimeout(() => setPhase(1), 700),
      setTimeout(() => setPhase(2), 1400),
      setTimeout(() => setPhase(3), 2500),
      setTimeout(() => setPhase(4), 3100),
      setTimeout(() => setPhase(5), 4400),
      setTimeout(() => { setVisible(false); onComplete?.(); }, 5600),
    ];
    return () => t.forEach(clearTimeout);
  }, [onComplete]);

  if (!visible) return null;

  return (
    <>
      {/* ── WIPE BARS (phase 5) - rendered ABOVE intro ── */}
      {phase >= 5 && <BroadcastWipe />}

      {/* ── INTRO OVERLAY - clips away from left as bars pass ── */}
      <motion.div className="fixed inset-0 z-[99] overflow-hidden"
        style={{ background: '#020203' }}
        animate={phase >= 5 ? {
          clipPath: [
            'polygon(0 0, 100% 0, 100% 100%, 0 100%)',
            'polygon(35% 0, 100% 0, 100% 100%, 20% 100%)',
            'polygon(85% 0, 100% 0, 100% 100%, 70% 100%)',
            'polygon(110% 0, 110% 0, 110% 100%, 110% 100%)',
          ]
        } : {}}
        transition={phase >= 5 ? { duration: 1.0, times: [0, 0.35, 0.7, 1], ease: [0.4, 0, 0.2, 1] } : {}}>

        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 65% 55% at 50% 50%, transparent 0%, rgba(2,2,3,1) 100%)' }} />

        {/* Goal Net */}
        <GoalNet impacted={phase >= 1 && phase < 3} />

        {/* ── SINGLE PUCK (flies from left, lands in center, no swap) ── */}
        <motion.div className="absolute z-20 flex flex-col items-center"
          style={{ top: '50%', left: '50%' }}
          initial={{ x: '-350%', y: '-50%' }}
          animate={{ x: '-50%', y: phase >= 4 ? 'calc(-50% - 40px)' : '-50%' }}
          transition={{
            x: { duration: 0.55, ease: [0.15, 0.85, 0.35, 1] },
            y: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
          }}>

          {/* Speed trail */}
          {phase < 1 && (
            <motion.div className="absolute top-1/2 -translate-y-1/2 right-full pointer-events-none"
              style={{ width: '180px', height: '3px', background: 'linear-gradient(90deg, transparent, rgba(70,199,255,0.15) 20%, rgba(255,255,255,0.5))', borderRadius: '2px' }}
              initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: [0, 0.6, 0.2], scaleX: 1 }}
              transition={{ duration: 0.4 }} />
          )}

          {/* Impact shake on the puck itself */}
          <motion.div
            animate={{
              scale: phase === 1 ? [1.08, 0.97, 1.02, 1] : phase >= 2 ? 1 : 1,
              x: phase === 1 ? [0, 3, -2, 1, 0] : 0,
            }}
            transition={{ scale: { duration: 0.3 }, x: { duration: 0.25 } }}>
            <HockeyPuck
              size={phase >= 2 ? 180 : 120}
              flipped={phase >= 2}
              bling={phase >= 3}
              logoLoaded={true}
              logoUrl={LOGO_URL} />
          </motion.div>

          {/* Shadow */}
          <motion.div className="mt-2 rounded-full"
            style={{ width: '90px', height: '5px', background: 'radial-gradient(ellipse, rgba(0,0,0,0.4), transparent)', filter: 'blur(2px)' }}
            animate={{ width: phase >= 2 ? 140 : 90, y: phase >= 4 ? -10 : 0, opacity: phase >= 4 ? 0.2 : 0.4 }}
            transition={{ duration: 0.5 }} />

          {/* Text */}
          <motion.div className="text-center"
            initial={{ height: 0, opacity: 0, marginTop: 0 }}
            animate={{ height: phase >= 4 ? 'auto' : 0, opacity: phase >= 4 ? 1 : 0, marginTop: phase >= 4 ? 20 : 0 }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}>
            <motion.div className="h-[2px] mx-auto rounded-full mb-4"
              style={{ background: '#CC0000', boxShadow: '0 0 10px rgba(204,0,0,0.4)' }}
              initial={{ width: 0 }} animate={{ width: phase >= 4 ? 60 : 0 }}
              transition={{ duration: 0.3, delay: 0.05 }} />
            <motion.div initial={{ opacity: 0, y: 15, filter: 'blur(6px)' }}
              animate={{ opacity: phase >= 4 ? 1 : 0, y: phase >= 4 ? 0 : 15, filter: phase >= 4 ? 'blur(0px)' : 'blur(6px)' }}
              transition={{ duration: 0.4, delay: 0.1 }}>
              <span className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white tracking-[-0.02em]" style={{ fontFamily: 'Space Grotesk' }}>DRESDEN</span>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 15, filter: 'blur(6px)' }}
              animate={{ opacity: phase >= 4 ? 1 : 0, y: phase >= 4 ? 0 : 15, filter: phase >= 4 ? 'blur(0px)' : 'blur(6px)' }}
              transition={{ duration: 0.4, delay: 0.2 }}>
              <span className="text-2xl sm:text-4xl lg:text-5xl font-bold text-[#CC0000] tracking-[-0.02em]" style={{ fontFamily: 'Space Grotesk' }}>DEVILS</span>
            </motion.div>
            <motion.p className="text-[10px] sm:text-xs text-white/20 tracking-[0.2em] uppercase mt-2" style={{ fontFamily: 'Space Grotesk' }}
              initial={{ opacity: 0 }} animate={{ opacity: phase >= 4 ? 1 : 0 }} transition={{ delay: 0.35, duration: 0.3 }}>
              EHV e.V. &bull; Est. 1997
            </motion.p>
            <motion.p className="text-xs sm:text-sm text-white/25 italic mt-3"
              initial={{ opacity: 0 }} animate={{ opacity: phase >= 4 ? 1 : 0 }} transition={{ delay: 0.5, duration: 0.3 }}>
              &ldquo;Leidenschaft auf dem Eis, Gemeinschaft im Verein!&rdquo;
            </motion.p>
          </motion.div>
        </motion.div>

        {/* Impact sparks */}
        {phase === 1 && <ImpactSparks />}

        {/* GOAL! (stays visible through flip) */}
        {phase >= 1 && phase < 3 && (
          <motion.div className="absolute top-[8%] sm:top-[10%] left-1/2 -translate-x-1/2 z-30"
            initial={{ opacity: 0, scale: 0.4, y: 15 }}
            animate={{
              opacity: phase >= 2 ? [1, 0.5, 0] : [0, 1, 1],
              scale: phase >= 2 ? [1, 0.9, 0.7] : [0.4, 1.15, 1],
              y: phase >= 2 ? [0, -5, -20] : [15, 0, 0],
            }}
            transition={{ duration: phase >= 2 ? 1.0 : 0.5, ease: 'easeOut' }}>
            <span className="text-4xl sm:text-6xl lg:text-7xl font-bold text-[#CC0000]"
              style={{ fontFamily: 'Space Grotesk', textShadow: '0 0 30px rgba(204,0,0,0.6), 0 0 60px rgba(204,0,0,0.3)' }}>
              GOAL!
            </span>
          </motion.div>
        )}

        {/* Red goal light */}
        {phase >= 1 && phase < 3 && (
          <motion.div className="absolute inset-0 z-10"
            style={{ background: 'radial-gradient(circle at 50% 45%, rgba(204,0,0,0.15), transparent 50%)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 2 ? [0.4, 0.15, 0] : [0, 1, 0, 0.6, 0, 0.3] }}
            transition={{ duration: phase >= 2 ? 0.8 : 1.0 }} />
        )}

        {/* Ambient */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-[3]">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div key={i} className="absolute rounded-full"
              style={{ width: 2, height: 2, background: `rgba(70,199,255,${0.06 + Math.random() * 0.08})`, left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
              animate={{ y: [0, -10, 0], opacity: [0.02, 0.12, 0.02] }}
              transition={{ duration: 3 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }} />
          ))}
        </div>
      </motion.div>
    </>
  );
}
